import { Link } from "react-router-dom";
import { Facebook, Twitter, Instagram, Linkedin } from "lucide-react";
import useFeatureFlag from "@/hooks/useFeatureFlag";

// Additional links configuration
const socialLinks = [
  { icon: Facebook, href: "#", label: "Facebook" },
  { icon: Twitter, href: "#", label: "Twitter" },
  { icon: Instagram, href: "#", label: "Instagram" },
  { icon: Linkedin, href: "#", label: "LinkedIn" },
];

const Footer = () => {
  // Add feature flags
  const isPricingPageEnabled = useFeatureFlag("pricingPage");
  const isSocialIconsEnabled = useFeatureFlag("footerSocialIcons");

  // Dynamic configuration object to control section visibility
  const navigationConfig = {
    company: {
      visible: true,
      title: "Company",
      links: [
        { to: "/about", label: "About Us" },
        { to: "/features", label: "Features" },
        ...(isPricingPageEnabled ? [{ to: "/pricing", label: "Pricing" }] : []),
        { to: "/contact", label: "Contact" },
      ],
    },
    legal: {
      visible: true,
      title: "Legal",
      links: [
        { to: "/privacy", label: "Privacy Policy" },
        { to: "/terms", label: "Terms of Service" },
      ],
    },
  };

  return (
    <footer
      className="relative w-full bg-gradient-to-b from-background/90 to-background border-t border-foreground/5 py-12 lg:py-24"
      data-oid="cje6j9j"
    >
      {/* Diagonal line accent */}
      <div
        className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-foreground/10 to-transparent"
        data-oid="n_06jtv"
      />

      <div
        className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8"
        data-oid="2p4hflu"
      >
        <div className="flex flex-col gap-12 lg:gap-16" data-oid="0fc1pma">
          {/* Main content section */}
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-8"
            data-oid="zns-ql1"
          >
            {/* Logo, description and social media */}
            <div
              className="md:col-span-1 lg:col-span-5 lg:col-start-2"
              data-oid="oitfqrt"
            >
              <div
                className="flex flex-col items-center md:items-start"
                data-oid="s2kfifi"
              >
                {/* Logo section */}
                <div
                  className="flex justify-center md:justify-start w-full pl-32 sm:pl-0 mb-[-60px]"
                  data-oid="l:jf2-x"
                >
                  <Link to="/" data-oid="1ahebef">
                    <div
                      className="h-[140px] w-[400px] flex items-center justify-center md:justify-start"
                      data-oid="r:o5bxd"
                    >
                      <img
                        src="/images/log_title.svg"
                        alt="Blyrha Logo"
                        className="object-contain invert-0 dark:invert w-full h-auto"
                        data-oid="wwkv18e"
                      />
                    </div>
                  </Link>
                </div>

                {/* Description and social links */}
                <div className="mt-0 space-y-5 w-full" data-oid="tjc79-r">
                  <p
                    className="text-muted-foreground text-sm sm:text-base leading-relaxed text-center md:text-left"
                    data-oid="k-a8nsh"
                  >
                    AI-powered platform that transforms your marketing workflow
                    in seconds. Experience the future of marketing automation.
                  </p>

                  {/* Social links - conditionally rendered based on feature flag */}
                  {isSocialIconsEnabled && (
                    <div
                      className="flex items-center justify-center md:justify-start gap-4 lg:gap-5"
                      data-oid="7n8--j2"
                    >
                      {socialLinks.map((social) => (
                        <Link
                          key={social.label}
                          to={social.href}
                          className="p-2 sm:p-3 rounded-full bg-foreground/5 hover:bg-foreground/10 transition-all duration-300 hover:scale-110"
                          aria-label={social.label}
                          data-oid="v4y:_cs"
                        >
                          <social.icon
                            className="w-4 h-4 sm:w-5 sm:h-5 text-foreground/70"
                            data-oid="xif4_lj"
                          />
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Navigation sections */}
            <div
              className="md:col-span-1 lg:col-span-5 flex justify-center md:justify-end"
              data-oid="yg8ebiw"
            >
              <div
                className="flex flex-col sm:flex-row gap-10 sm:gap-16 lg:gap-24"
                data-oid="06e22_c"
              >
                {/* Company links */}
                {navigationConfig.company.visible && (
                  <div
                    className="space-y-4 text-center md:text-left"
                    data-oid="utur0j:"
                  >
                    <h3
                      className="text-lg font-semibold text-foreground"
                      data-oid="q._v.3d"
                    >
                      {navigationConfig.company.title}
                    </h3>
                    <ul className="space-y-2 sm:space-y-3" data-oid="sj8-2lw">
                      {navigationConfig.company.links.map((link) => (
                        <li key={link.to} data-oid=":-9pt67">
                          <Link
                            to={link.to}
                            className="text-sm sm:text-base text-muted-foreground hover:text-foreground transition-colors duration-200"
                            data-oid="rg5lunr"
                          >
                            {link.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Legal links */}
                {navigationConfig.legal.visible && (
                  <div
                    className="space-y-4 text-center md:text-left"
                    data-oid="1jo_jz4"
                  >
                    <h3
                      className="text-lg font-semibold text-foreground"
                      data-oid=".-8c:tw"
                    >
                      {navigationConfig.legal.title}
                    </h3>
                    <ul className="space-y-2 sm:space-y-3" data-oid="d25s_f-">
                      {navigationConfig.legal.links.map((link) => (
                        <li key={link.to} data-oid="diih2lx">
                          <Link
                            to={link.to}
                            className="text-sm sm:text-base text-muted-foreground hover:text-foreground transition-colors duration-200"
                            data-oid="lx6delv"
                          >
                            {link.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div
            className="pt-6 md:pt-8 border-t border-foreground/5"
            data-oid="ysypab9"
          >
            <div
              className="flex flex-col md:flex-row justify-between items-center gap-4"
              data-oid="-_ha1lv"
            >
              <p
                className="text-xs sm:text-sm text-muted-foreground order-2 md:order-1 text-center md:text-left"
                data-oid="py:nk0s"
              >
                &copy; {new Date().getFullYear()} Blyrha Inc. All rights
                reserved.
              </p>
              <div
                className="flex items-center gap-4 sm:gap-8 order-1 md:order-2"
                data-oid="upcvfsr"
              >
                <Link
                  to="/privacy"
                  className="text-xs sm:text-sm text-muted-foreground hover:text-foreground transition-colors duration-200"
                  data-oid="bh.14-7"
                >
                  Privacy Policy
                </Link>
                <Link
                  to="/terms"
                  className="text-xs sm:text-sm text-muted-foreground hover:text-foreground transition-colors duration-200"
                  data-oid=":ojtng0"
                >
                  Terms of Service
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
